import { defineStore } from "pinia";
import { POLICY_HISTORY_FILTER_ALL, type PolicyHistoryFilter } from "@price-rescue/api-contracts";

import { MerchantApiError } from "../authentication/errors.js";
import {
  defaultActivationDraft,
  defaultMerchantPolicySettings,
  defaultOverrideDraft,
  defaultPolicyPreviewSample,
  policyFromMerchantSettings,
  type MerchantPolicyHistory,
  type MerchantPolicyHistoryList,
  type MerchantPolicyPage,
  type MerchantPolicySettings,
  type MerchantPolicyValidation,
  type PolicyActivationDraft,
  type PolicyOverrideDraft,
  type PolicyPreviewResult,
  type PolicyPreviewSample,
  type PolicyValidationIssue,
  type PriceMatchPolicyDto,
} from "./models.js";
import type { PoliciesService } from "./service.js";

export const usePoliciesStore = defineStore("merchant-policies", {
  state: (): {
    activationDraft: PolicyActivationDraft;
    errorMessage?: string;
    fieldErrors: PolicyValidationIssue[];
    historyDateFrom: string;
    historyDateTo: string;
    historyErrorMessage?: string;
    historyFilter: PolicyHistoryFilter;
    historyLoading: boolean;
    historyPageSize: number;
    historyPagination: MerchantPolicyHistoryList["pagination"];
    historyRows: MerchantPolicyHistory[];
    page?: MerchantPolicyPage;
    settingsDraft: MerchantPolicySettings;
    loading: boolean;
    mutationMessage?: string;
    overrideDraft: PolicyOverrideDraft;
    overrideDeleting: boolean;
    overrideErrorMessage?: string;
    overrideLoadSequence: number;
    overrideLoading: boolean;
    overrideSaving: boolean;
    previewResult?: PolicyPreviewResult;
    previewSample: PolicyPreviewSample;
    previewing: boolean;
    savingSettings: boolean;
    service?: PoliciesService;
    settingsSaveErrorMessage?: string;
    validatingSettings: boolean;
    validation?: MerchantPolicyValidation;
  } => ({
    activationDraft: defaultActivationDraft(),
    fieldErrors: [],
    historyDateFrom: "",
    historyDateTo: "",
    historyFilter: POLICY_HISTORY_FILTER_ALL,
    historyLoading: false,
    historyPageSize: 25,
    historyPagination: { currentCursor: null, nextCursor: null },
    historyRows: [],
    settingsDraft: defaultMerchantPolicySettings(),
    loading: false,
    overrideDraft: defaultOverrideDraft(),
    overrideDeleting: false,
    overrideLoadSequence: 0,
    overrideLoading: false,
    overrideSaving: false,
    previewSample: defaultPolicyPreviewSample(),
    previewing: false,
    savingSettings: false,
    validatingSettings: false,
  }),
  getters: {
    canActivateSettings: (state): boolean =>
      state.validation?.valid !== false &&
      state.activationDraft.acknowledgedLegalReview &&
      state.activationDraft.reason.trim().length > 0 &&
      (state.settingsDraft.minimumMarginBps === undefined ||
        state.activationDraft.acknowledgedMarginRisk) &&
      state.page?.allowedActions.canActivate !== false,
    hasSettingsChanges: (state): boolean =>
      JSON.stringify(state.settingsDraft) !==
      JSON.stringify(state.page?.settings ?? defaultMerchantPolicySettings()),
    settingsMarginRulesRequireAcknowledgement: (state): boolean =>
      state.settingsDraft.minimumMarginBps !== undefined,
  },
  actions: {
    configure(service: PoliciesService): void {
      this.service = service;
    },
    async loadPolicyPage(): Promise<void> {
      this.loading = true;
      this.clearTransientState();
      try {
        this.applyPolicyPage(await this.requireService().loadPolicyPage());
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Pricing policies could not be loaded.");
      } finally {
        this.loading = false;
      }
    },
    async refreshPolicyPageForOverrides(): Promise<void> {
      this.overrideLoading = true;
      delete this.overrideErrorMessage;
      try {
        this.applyPolicyPage(await this.requireService().loadPolicyPage());
      } catch (error: unknown) {
        this.overrideErrorMessage = messageForError(
          error,
          "Product overrides could not be refreshed.",
        );
      } finally {
        this.overrideLoading = false;
      }
    },
    async loadPolicyHistory(
      input?: Partial<{ filter: PolicyHistoryFilter; cursor: string }>,
    ): Promise<void> {
      const filter = input?.filter ?? this.historyFilter;
      const cursor = input?.cursor;
      this.historyLoading = true;
      delete this.historyErrorMessage;
      try {
        const page = await this.requireService().loadPolicyHistory({
          filter,
          limit: this.historyPageSize,
          ...(this.historyDateFrom.length === 0 ? {} : { dateFrom: this.historyDateFrom }),
          ...(this.historyDateTo.length === 0 ? {} : { dateTo: this.historyDateTo }),
          ...(cursor === undefined ? {} : { cursor }),
        });
        this.applyPolicyHistoryPage(page, cursor !== undefined);
      } catch (error: unknown) {
        this.historyErrorMessage = messageForError(error, "Policy history could not be loaded.");
      } finally {
        this.historyLoading = false;
      }
    },
    updateHistoryFilter(filter: PolicyHistoryFilter): void {
      this.historyFilter = filter;
      delete this.historyErrorMessage;
    },
    updateHistoryDateRange(dateRange: { dateFrom: string; dateTo: string }): void {
      this.historyDateFrom = dateRange.dateFrom;
      this.historyDateTo = dateRange.dateTo;
      delete this.historyErrorMessage;
    },
    async applyHistoryFilters(): Promise<void> {
      this.historyRows = [];
      this.historyPagination = { currentCursor: null, nextCursor: null };
      await this.loadPolicyHistory({ filter: this.historyFilter });
    },
    async clearHistoryFilters(): Promise<void> {
      this.historyFilter = POLICY_HISTORY_FILTER_ALL;
      this.historyDateFrom = "";
      this.historyDateTo = "";
      this.historyRows = [];
      this.historyPagination = { currentCursor: null, nextCursor: null };
      await this.loadPolicyHistory({ filter: this.historyFilter });
    },
    async loadNextHistoryPage(): Promise<void> {
      const cursor = this.historyPagination.nextCursor;
      if (cursor === null || this.historyLoading) {
        return;
      }

      await this.loadPolicyHistory({ filter: this.historyFilter, cursor });
    },
    async changeHistoryPageSize(pageSize: number): Promise<void> {
      this.historyPageSize = normalizePageSize(pageSize);
      this.historyRows = [];
      this.historyPagination = { currentCursor: null, nextCursor: null };
      await this.loadPolicyHistory({ filter: this.historyFilter });
    },
    updateSettingsDraft(patch: Partial<MerchantPolicySettings>): void {
      this.settingsDraft = { ...this.settingsDraft, ...patch };
      delete this.validation;
      delete this.settingsSaveErrorMessage;
      this.fieldErrors = [];
    },
    resetSettingsDraftToDefaults(): void {
      this.settingsDraft = defaultMerchantPolicySettings();
      delete this.validation;
      this.fieldErrors = [];
      this.mutationMessage = "Default pricing policy loaded as a draft. Save policy to apply it.";
    },
    async validateSettingsDraft(): Promise<void> {
      this.validatingSettings = true;
      delete this.errorMessage;
      try {
        this.validation = await this.requireService().validateSettings(this.settingsDraft);
        this.fieldErrors = this.validation.errors;
        if (this.validation.valid) {
          this.mutationMessage = "Policy draft validated.";
        }
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Policy draft could not be validated.");
      } finally {
        this.validatingSettings = false;
      }
    },
    async activateSettings(): Promise<void> {
      if (!this.canActivateSettingsWithoutFieldValidation()) {
        this.errorMessage = this.settingsActivationErrorMessage();
        return;
      }

      this.savingSettings = true;
      delete this.errorMessage;
      delete this.mutationMessage;
      delete this.settingsSaveErrorMessage;
      try {
        const activationInput = {
          settings: this.settingsDraft,
          reason: this.activationDraft.reason,
          acknowledgedMarginRisk: this.activationDraft.acknowledgedMarginRisk,
          acknowledgedLegalReview: this.activationDraft.acknowledgedLegalReview,
        };
        const page = await this.requireService().activateSettings(activationInput);
        this.applyPolicyPage(page);
        this.mutationMessage = "Pricing policy saved and activated.";
      } catch (error: unknown) {
        this.applyMutationError(error);
      } finally {
        this.savingSettings = false;
      }
    },
    async previewSettingsPolicy(
      sample: PolicyPreviewSample = defaultPolicyPreviewSample(),
    ): Promise<void> {
      this.previewing = true;
      delete this.previewResult;
      delete this.errorMessage;
      try {
        this.previewResult = await this.requireService().previewPolicy(
          policyFromMerchantSettings(this.settingsDraft),
          sample,
        );
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Policy preview could not be evaluated.");
      } finally {
        this.previewing = false;
      }
    },
    async loadProductOverride(): Promise<void> {
      if (
        this.overrideDraft.scopeId.trim().length === 0 ||
        (this.overrideDraft.scope === "VARIANT" && this.overrideDraft.variantId === undefined)
      )
        return;
      const target = this.currentOverrideTarget();
      this.overrideLoadSequence += 1;
      const sequence = this.overrideLoadSequence;
      this.overrideLoading = true;
      delete this.overrideErrorMessage;
      try {
        const dto = await this.requireService().loadProductPolicy(
          this.overrideDraft.scopeId.trim(),
          this.overrideDraft.variantId,
        );
        if (!this.isCurrentOverrideTarget(target, sequence)) return;
        this.overrideDraft = {
          scope: dto.scope === "VARIANT" ? "VARIANT" : "PRODUCT",
          scopeId: this.overrideDraft.scopeId.trim(),
          ...(this.overrideDraft.variantId === undefined
            ? {}
            : { variantId: this.overrideDraft.variantId }),
          configured: dto.configured,
          enabled: dto.enabled,
          policyOverride: productOverrideFieldsFromDto(dto),
        };
      } catch (error: unknown) {
        if (!this.isCurrentOverrideTarget(target, sequence)) return;
        if (isMissingProductOverride(error)) {
          this.overrideDraft = {
            ...defaultOverrideDraft(),
            scopeId: this.overrideDraft.scopeId.trim(),
            ...(this.overrideDraft.variantId === undefined
              ? {}
              : { variantId: this.overrideDraft.variantId }),
          };
          return;
        }

        this.overrideErrorMessage = messageForError(error, "Product override could not be loaded.");
      } finally {
        if (this.isCurrentOverrideTarget(target, sequence)) this.overrideLoading = false;
      }
    },
    async loadProductOverrideForProduct(productId: string): Promise<void> {
      this.overrideDraft = {
        ...defaultOverrideDraft(),
        scopeId: productId.trim(),
      };
      await this.loadProductOverride();
    },
    async loadVariantOverrideForVariant(productId: string, variantId: string): Promise<void> {
      this.overrideDraft = {
        ...defaultOverrideDraft(),
        scope: "VARIANT",
        scopeId: productId.trim(),
        variantId: variantId.trim(),
      };
      await this.loadProductOverride();
    },
    async saveOverride(): Promise<void> {
      if (this.overrideDraft.scopeId.trim().length === 0) {
        this.overrideErrorMessage = "Enter a product id before saving the override.";
        return;
      }

      this.overrideSaving = true;
      delete this.overrideErrorMessage;
      delete this.mutationMessage;
      try {
        const dto = await this.requireService().saveProductOverride({
          ...this.overrideDraft,
          scopeId: this.overrideDraft.scopeId.trim(),
        });
        this.overrideDraft = {
          ...this.overrideDraft,
          scope: dto.scope === "VARIANT" ? "VARIANT" : "PRODUCT",
          configured: true,
          enabled: dto.enabled,
          policyOverride: dto.policy,
        };
        this.mutationMessage =
          this.overrideDraft.variantId === undefined
            ? "Product policy override saved."
            : "Variant policy override saved.";
      } catch (error: unknown) {
        this.overrideErrorMessage = messageForError(error, "Product override could not be saved.");
      } finally {
        this.overrideSaving = false;
      }
    },
    async deleteOverride(): Promise<void> {
      const productId = this.overrideDraft.scopeId.trim();
      if (productId.length === 0) {
        this.overrideErrorMessage = "Select a product override before deleting it.";
        return;
      }

      this.overrideDeleting = true;
      delete this.overrideErrorMessage;
      delete this.mutationMessage;
      try {
        await this.requireService().deleteProductOverride(productId, this.overrideDraft.variantId);
        this.overrideDraft = {
          ...defaultOverrideDraft(),
          ...(this.overrideDraft.variantId === undefined
            ? {}
            : { scope: "VARIANT" as const, variantId: this.overrideDraft.variantId }),
          scopeId: productId,
        };
        this.mutationMessage =
          this.overrideDraft.variantId === undefined
            ? "Product policy override removed."
            : "Variant policy override removed.";
      } catch (error: unknown) {
        this.overrideErrorMessage = messageForError(
          error,
          "Product override could not be removed.",
        );
      } finally {
        this.overrideDeleting = false;
      }
    },
    resetPreviewSample(): void {
      this.previewSample = defaultPolicyPreviewSample(this.previewSample.currency);
      delete this.previewResult;
    },
    resetOverrideDraft(): void {
      this.overrideLoadSequence += 1;
      this.overrideDraft = defaultOverrideDraft();
    },
    currentOverrideTarget(): { productId: string; variantId?: string } {
      return {
        productId: this.overrideDraft.scopeId.trim(),
        ...(this.overrideDraft.variantId === undefined
          ? {}
          : { variantId: this.overrideDraft.variantId }),
      };
    },
    isCurrentOverrideTarget(
      target: { productId: string; variantId?: string },
      sequence: number,
    ): boolean {
      const current = this.currentOverrideTarget();
      return (
        this.overrideLoadSequence === sequence &&
        current.productId === target.productId &&
        current.variantId === target.variantId
      );
    },
    applyPolicyPage(page: MerchantPolicyPage): void {
      this.page = page;
      this.historyRows = [...page.history];
      this.historyPagination = { currentCursor: null, nextCursor: null };
      this.settingsDraft = { ...page.settings };
      this.activationDraft = defaultActivationDraft();
      delete this.validation;
      delete this.settingsSaveErrorMessage;
      this.fieldErrors = [];
    },
    applyPolicyHistoryPage(page: MerchantPolicyHistoryList, append: boolean): void {
      this.historyFilter = page.filters.filter;
      this.historyRows = append ? [...this.historyRows, ...page.items] : [...page.items];
      this.historyPagination = page.pagination;
    },
    clearTransientState(): void {
      delete this.errorMessage;
      delete this.mutationMessage;
      delete this.settingsSaveErrorMessage;
      delete this.overrideErrorMessage;
    },
    applyMutationError(error: unknown): void {
      const validationErrors = validationErrorsFromMerchantApiError(error);
      if (validationErrors.length > 0) {
        this.fieldErrors = validationErrors;
        this.validation = {
          valid: false,
          errors: validationErrors,
          warnings: [],
          protectionChecks: [],
        };
      }
      this.settingsSaveErrorMessage = messageForError(error, "Policy change could not be saved.");
    },
    settingsActivationErrorMessage(): string {
      if (this.page?.allowedActions.canActivate === false)
        return (
          this.page.allowedActions.disabledReason ??
          "You do not have permission to activate pricing policies."
        );
      if (this.activationDraft.reason.trim().length === 0)
        return "Add a short change note before saving this policy.";
      if (this.fieldErrors.length > 0 || this.validation?.valid === false)
        return "Fix the highlighted policy fields before activation.";
      if (
        this.settingsDraft.minimumMarginBps !== undefined &&
        !this.activationDraft.acknowledgedMarginRisk
      )
        return "Acknowledge margin-floor behavior before activation.";
      if (!this.activationDraft.acknowledgedLegalReview)
        return "Acknowledge pricing-policy review before activation.";
      return "Policy cannot be activated yet.";
    },
    canActivateSettingsWithoutFieldValidation(): boolean {
      return (
        this.activationDraft.acknowledgedLegalReview &&
        this.activationDraft.reason.trim().length > 0 &&
        (this.settingsDraft.minimumMarginBps === undefined ||
          this.activationDraft.acknowledgedMarginRisk) &&
        this.page?.allowedActions.canActivate !== false
      );
    },
    requireService(): PoliciesService {
      if (this.service === undefined) {
        throw new Error("Policies store was not configured before use.");
      }
      return this.service;
    },
  },
});

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

function normalizePageSize(pageSize: number): 25 | 50 | 100 {
  return PAGE_SIZE_OPTIONS.includes(pageSize as 25 | 50 | 100) ? (pageSize as 25 | 50 | 100) : 25;
}

function productOverrideFieldsFromDto(
  dto: PriceMatchPolicyDto,
): PolicyOverrideDraft["policyOverride"] {
  if (dto.scope === "VARIANT") {
    return dto.configured === false ? {} : { ...dto.policy };
  }

  if (!dto.configured) {
    return defaultOverrideDraft().policyOverride;
  }

  const policyOverride: Partial<PolicyOverrideDraft["policyOverride"]> = {
    ...(dto.policy.maxDiscountBps === undefined
      ? {}
      : { maxDiscountBps: dto.policy.maxDiscountBps }),
    ...(dto.policy.allowCounteroffer === undefined
      ? {}
      : { allowCounteroffer: dto.policy.allowCounteroffer }),
    ...(dto.policy.allowManualReview === undefined
      ? {}
      : { allowManualReview: dto.policy.allowManualReview }),
  };

  if (dto.policy.minimumMarginBps !== undefined) {
    policyOverride.minimumMarginBps = dto.policy.minimumMarginBps;
  }

  return policyOverride;
}

function isMissingProductOverride(error: unknown): boolean {
  return (
    error instanceof MerchantApiError &&
    error.details?.["state"] === "MISSING_PRODUCT_POLICY_OVERRIDE"
  );
}

function messageForError(error: unknown, fallback: string): string {
  if (error instanceof MerchantApiError && error.kind === "conflict")
    return "This policy changed while you were editing. Your draft is preserved for comparison.";
  if (error instanceof MerchantApiError && error.kind === "validation") return error.message;
  if (error instanceof MerchantApiError && error.kind === "access")
    return "You do not have permission to manage pricing policies.";
  if (error instanceof Error) return error.message;
  return fallback;
}

function validationErrorsFromMerchantApiError(error: unknown): PolicyValidationIssue[] {
  if (!(error instanceof MerchantApiError) || error.kind !== "validation") {
    return [];
  }

  const errors = error.details?.errors;
  if (!Array.isArray(errors)) {
    return [];
  }

  return errors.flatMap(toPolicyValidationIssue);
}

function toPolicyValidationIssue(value: unknown): PolicyValidationIssue[] {
  if (!isValidationIssueRecord(value)) {
    return [];
  }

  return [{ field: value.field, message: value.message }];
}

function isValidationIssueRecord(value: unknown): value is { field: string; message: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    "field" in value &&
    "message" in value &&
    typeof value.field === "string" &&
    typeof value.message === "string"
  );
}
